import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ImageUploaderProps {
  /** 当前图片 URL */
  value?: string;
  /** 上传成功后回调，返回公开 URL */
  onUpload: (url: string) => void;
  /** 上传按钮样式：'button' 普通按钮 | 'area' 方形区域 */
  variant?: 'button' | 'area';
  /** 方形区域尺寸（variant=area 时生效） */
  size?: number;
  /** 提示文字 */
  placeholder?: string;
  /** 上传路径前缀，默认 'uploads' */
  folder?: string;
  /** 是否禁用 */
  disabled?: boolean;
}

const ImageUploader = ({
  value,
  onUpload,
  variant = 'button',
  size = 80,
  placeholder = '上传图片',
  folder = 'uploads',
  disabled = false,
}: ImageUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 校验文件类型
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    // 校验文件大小（10MB）
    if (file.size > 10 * 1024 * 1024) {
      setError('图片不能超过 10MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(fileName, file, { upsert: false, contentType: file.type });

      if (uploadError) {
        setError('上传失败，请重试');
        return;
      }

      const { data } = supabase.storage.from('images').getPublicUrl(fileName);
      onUpload(data.publicUrl);
    } catch {
      setError('上传失败，请重试');
    } finally {
      setUploading(false);
      // 清空 input，允许重复上传同一文件
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const triggerUpload = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  if (variant === 'area') {
    return (
      <div>
        <div
          onClick={triggerUpload}
          className={`relative rounded-lg overflow-hidden border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
            uploading ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-rose-300 hover:bg-rose-50/30'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ width: size, height: size }}
        >
          {value ? (
            <>
              <img src={value} alt="preview" className="w-full h-full object-cover object-top" />
              {!disabled && (
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="ri-upload-cloud-line text-white text-xl" />
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {uploading ? (
                <div className="w-5 h-5 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <i className="ri-upload-cloud-line text-gray-300 text-xl mb-1" />
                  <span className="text-gray-400 text-center leading-tight px-1" style={{ fontSize: '10px' }}>{placeholder}</span>
                </>
              )}
            </>
          )}
        </div>
        {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={disabled || uploading}
        />
      </div>
    );
  }

  // variant === 'button'
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={triggerUpload}
        disabled={disabled || uploading}
        className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {uploading ? (
          <>
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            上传中...
          </>
        ) : (
          <>
            <i className="ri-upload-cloud-line text-base" />
            {placeholder}
          </>
        )}
      </button>
      {value && (
        <span className="text-xs text-gray-400 truncate max-w-[160px]" title={value}>
          已上传
        </span>
      )}
      {error && <span className="text-xs text-rose-500">{error}</span>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled || uploading}
      />
    </div>
  );
};

export default ImageUploader;
